import { storageHelper, User } from '@spo/logic';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAmplitudeDayLog } from '../../hooks/amplitude/useAmplitudeDayLog';
import { useAmplitudeLogMember } from '../../hooks/amplitude/useAmplitudeLogMember';
import { useAmplitudeUser } from '../../hooks/amplitude/useAmplitudeUser';
import AuthClientRequest from '../../utils/authClientRequest/auth-client-request';
import {
  buildQueryParameterUrl,
  getQueryParameterByName,
} from '../../utils/url';
import { ErrorPage } from '../errorPage/ErrorPage';

export const AUTHENTICATION_STATE_KEY = 'authentication_state';

export enum AUTHENTICATION_CODE {
  LANDING,
  ERROR,
  AUTHENTICATED,
}

export interface AuthenticationStatus {
  code: AUTHENTICATION_CODE;
  status?: number;
}

export const redirectTo = (url: string | null) => {
  url
    ? window.location.replace(url)
    : window.location.replace('/personal-offer');
};

export const AuthenticationPage = () => {
  const location = useLocation();

  useAmplitudeDayLog();
  useAmplitudeLogMember();
  useAmplitudeUser();

  const [authenticationStatus, setAuthenticationStatus] =
    useState<AuthenticationStatus>({
      code: AUTHENTICATION_CODE.LANDING,
    });
  const fetchTokenAndRedirect = (code: string): void => {
    const redirectUri = `${window.location.origin}/personal-offer/authentication`;
    AuthClientRequest.get(
      `/api/user/token?${buildQueryParameterUrl({
        code,
        redirectUri,
      })}`,
      { withCredentials: true },
    ).subscribe({
      next: (userInformation: User) => {
        storageHelper.setItem('is_authenticated', true);
        storageHelper.setItem('user', JSON.stringify(userInformation));
        setAuthenticationStatus({ code: AUTHENTICATION_CODE.AUTHENTICATED });
        redirectTo(storageHelper.getItem('before_signin_url'));
      },
      error: (error: { status?: number }) => {
        setAuthenticationStatus({
          code: AUTHENTICATION_CODE.ERROR,
          status: error.status,
        });
      },
    });
  };

  useEffect(() => {
    const codeUriParameter = getQueryParameterByName('code', location.search);
    if (
      codeUriParameter &&
      authenticationStatus.code === AUTHENTICATION_CODE.LANDING &&
      storageHelper.getItem('is_fetching_token') !== null
    ) {
      fetchTokenAndRedirect(codeUriParameter);
    }
    return () => {
      storageHelper.removeItem(AUTHENTICATION_STATE_KEY);
      storageHelper.removeItem('is_fetching_token');
    };
  }, []);

  if (authenticationStatus.code === AUTHENTICATION_CODE.ERROR) {
    return (
      <ErrorPage
        errorPageTitle="Authentication Error"
        errorMessage={authenticationStatus.status?.toString() ?? ''}
      />
    );
  } else if (authenticationStatus.code === AUTHENTICATION_CODE.LANDING) {
    return (
      <p data-testid="authentication-message">Authentication in progress...</p>
    );
  } else {
    return (
      <p data-testid="authentication-message">
        Authenticated ! You&apos;ll be redirected.
      </p>
    );
  }
};
